import Phaser from "phaser";
import { applyTerminalGreenFilter } from "../effects/profile-image-filter.js";
import type { ProfileRedactionConfig } from "../types.js";
import type { TerminalAudio } from "../effects/terminal-audio.js";
import {
    ENHANCE_SCANLINE_DELAY_MS,
    PADDING,
    PROFILE_TEXTURE_FILTERED_ENHANCED_KEY,
    PROFILE_TEXTURE_FILTERED_KEY,
    PROFILE_TEXTURE_KEY,
    SCANLINE_DELAY_MS,
    SCANLINE_STEP_PX,
} from "./constants.js";

export class TerminalImageDisplay {
    private scanlineTimer?: Phaser.Time.TimerEvent;

    constructor(
        private readonly scene: Phaser.Scene,
        private readonly output: Phaser.GameObjects.Container,
        private readonly terminalAudio: TerminalAudio,
        private readonly getNextLineY: () => number,
        private readonly setNextLineY: (y: number) => void,
        private readonly setScrollPinBottom: (value: boolean) => void,
        private readonly scrollOutput: () => void,
        private readonly outputMaxWidth: () => number,
        private readonly profileImage: string,
        private readonly profileRedaction?: ProfileRedactionConfig,
    ) {}

    stopScanlineReveal(): void {
        this.scanlineTimer?.destroy();
        this.scanlineTimer = undefined;
    }

    displayScanlineImage(src: string, textureKey = PROFILE_TEXTURE_KEY): void {
        this.displayImage(src, textureKey, "scanline", false);
    }

    displayEnhancedImage(src: string, textureKey = PROFILE_TEXTURE_KEY): void {
        this.displayImage(src, textureKey, "scanline", true);
    }

    private displayImage(
        src: string,
        textureKey: string,
        mode: "scanline" | "full",
        enhanced: boolean,
    ): void {
        const begin = (): void => {
            if (!this.scene.textures.exists(textureKey)) return;
            this.paintImage(src, textureKey, mode, enhanced);
        };

        if (this.scene.textures.exists(textureKey)) {
            begin();
            return;
        }

        this.terminalAudio.beginPictureRead();
        this.scene.load.image(textureKey, src);
        this.scene.load.once(Phaser.Loader.Events.COMPLETE, () => {
            this.terminalAudio.endPictureRead();
            begin();
        });
        this.scene.load.start();
    }

    private paintImage(
        src: string,
        textureKey: string,
        mode: "scanline" | "full",
        enhanced = false,
    ): void {
        if (!this.scene.textures.exists(textureKey)) return;

        const filteredKey = enhanced
            ? PROFILE_TEXTURE_FILTERED_ENHANCED_KEY
            : PROFILE_TEXTURE_FILTERED_KEY;
        const displayKey = applyTerminalGreenFilter(
            this.scene,
            textureKey,
            filteredKey,
            enhanced
                ? { pixelSize: 1 }
                : this.redactionForSource(src),
        );
        const frame = this.scene.textures.getFrame(displayKey);
        const topY = this.getNextLineY();
        const img = this.scene.add.image(PADDING, topY, displayKey);
        const maxW = this.outputMaxWidth();
        const scale = Math.min(1, maxW / frame.width);
        img.setScale(scale);
        img.setOrigin(0, 0);
        this.output.add(img);

        const displayH = frame.height * scale;
        this.setNextLineY(topY + displayH + 8);
        this.setScrollPinBottom(true);

        if (mode === "full") {
            img.setCrop(0, 0, frame.width, frame.height);
            this.scrollOutput();
            return;
        }

        img.setCrop(0, 0, frame.width, 0);

        let cropH = 0;
        const steps = Math.ceil(frame.height / SCANLINE_STEP_PX);
        this.stopScanlineReveal();

        const scanlineDelay = enhanced ? ENHANCE_SCANLINE_DELAY_MS : SCANLINE_DELAY_MS;
        this.scanlineTimer = this.scene.time.addEvent({
            delay: scanlineDelay,
            repeat: Math.max(0, steps - 1),
            callback: () => {
                cropH = Math.min(cropH + SCANLINE_STEP_PX, frame.height);
                img.setCrop(0, 0, frame.width, cropH);
                this.scrollOutput();
            },
        });
    }

    private redactionForSource(src: string): { redaction: ProfileRedactionConfig } | undefined {
        if (!this.profileRedaction || src !== this.profileImage) {
            return undefined;
        }
        return { redaction: this.profileRedaction };
    }
}
